import type { DocumentData } from '@/store/dataCatalogSlice'

/**
 * Upload payload helpers for the V2 region-selection flow.
 *
 * Both exist because V2 dropped the two signals the backend uses to decide
 * whether a P&ID PDF is fanned out into one job per page
 * (`async-jobs-bridge.service.ts`: `hasPerPageBboxes || totalPdfPages > 1`).
 * The legacy modal path sends both; V2 sent neither, so a multi-page drawing
 * uploaded with region selection was processed as page 1 alone.
 */

/** A bbox as sent to the API — `page` present only on multi-page uploads. */
export interface UploadBbox {
  x: number
  y: number
  width: number
  height: number
  type: string
  page?: number
}

/**
 * Strip the `id`/`name` fields RegionSelectionModal adds for its own canvas
 * bookkeeping, KEEPING `page`.
 *
 * `page` is not cosmetic: it is what tells the backend this box belongs to
 * sheet N, and dropping it silently collapsed a multi-sheet upload down to its
 * first page. The old destructure listed five fields and lost it — invisibly,
 * because the bbox types omitted `page` too.
 */
export function cleanBboxes(bboxes: DocumentData['bboxes']): UploadBbox[] | null {
  if (!bboxes || bboxes.length === 0) return null
  return bboxes.map(({ x, y, width, height, type, page }) => ({
    x,
    y,
    width,
    height,
    type,
    ...(page != null && { page }),
  }))
}

/**
 * The page-map document properties for a multi-page PDF, or undefined when the
 * upload is a single sheet and needs none.
 *
 * Shapes match the legacy path byte for byte — `total_pdf_pages` as a string,
 * `pdf_page_numbers` as a JSON array string — because the backend parses these
 * exact forms and the two flows must stay interchangeable.
 */
export function pageMapProperties(
  totalPdfPages?: number,
  pdfPageNumbers?: number[]
): { total_pdf_pages: string; pdf_page_numbers?: string } | undefined {
  if (!totalPdfPages || totalPdfPages <= 1) return undefined
  return {
    total_pdf_pages: String(totalPdfPages),
    ...(pdfPageNumbers && pdfPageNumbers.length > 0 && {
      pdf_page_numbers: JSON.stringify(pdfPageNumbers),
    }),
  }
}
